import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import {
  cancelEmployeeAdvance,
  deleteEmployeeAdvance,
  disburseEmployeeAdvance,
  getEmployeeAdvances,
  refundEmployeeAdvance,
} from "features/EmployeeAdvances/services/EmployeeAdvanceService";
import DeleteModal from "components/common/DeleteModal";
import { EmployeeAdvance } from "features/EmployeeAdvances/types/employee-advance.types";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import {
  EmployeeAdvanceStatus,
  employeeAdvanceStatusMap,
} from "features/EmployeeAdvances/constants/employee-advance.constants";
import { CodeCell, IndexColumn } from "components/common/columns";
import { DateTime } from "components/common/Date";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import CancelModal from "../CancelModal";
import DisburseModal from "../DisburseModal";
import RefundModal from "../RefundModal";
import { formatCurrency } from "utils/currency";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import EmployeeAdvanceListFilters from "./EmployeeAdvanceListFilters";

function employeeName(r: EmployeeAdvance) {
  const src = r.employee;
  if (!src) return `#${r.employeeId}`;
  const fn = src.firstName ?? src.user?.firstName ?? "";
  const ln = src.lastName ?? src.user?.lastName ?? "";
  const name = [fn, ln].filter(Boolean).join(" ").trim();
  return name || src.code?.trim() || `#${r.employeeId}`;
}

export const EmployeeAdvancesTable = () => {
  const { can } = useAccess();
  const canReadAdv = can(Permissions.EMPLOYEE_ADVANCE.READ);
  const canReadEmployee = can(Permissions.EMPLOYEE.READ);
  const canUpdateAdv = can(Permissions.EMPLOYEE_ADVANCE.UPDATE);
  const canDeleteAdv = can(Permissions.EMPLOYEE_ADVANCE.DELETE);
  const canCancelAdv = can(Permissions.EMPLOYEE_ADVANCE.CANCEL);
  const canDisburseAdv = can(Permissions.EMPLOYEE_ADVANCE.DISBURSE);
  const canRefundAdv = can(Permissions.EMPLOYEE_ADVANCE.REFUND);
  const tableRef = useRef<() => void>(null);
  const [advance, setAdvance] = useState<EmployeeAdvance | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState("");
  const [disburseModal, setDisburseModal] = useState<boolean>(false);
  const [disburseSubmitting, setDisburseSubmitting] = useState(false);
  const [refundModal, setRefundModal] = useState<boolean>(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  const requestParams = useMemo(() => ({ status: status || undefined }), [status]);
  const filterCount = [status].filter(Boolean).length;

  const onClickDelete = useCallback((record: EmployeeAdvance) => {
    setAdvance(record);
    setDeleteModal(true);
  }, []);

  const onClickCancel = useCallback((record: EmployeeAdvance) => {
    setAdvance(record);
    setCancelReason("");
    setCancelModal(true);
  }, []);

  const onClickDisburse = useCallback((record: EmployeeAdvance) => {
    setAdvance(record);
    setDisburseModal(true);
  }, []);

  const onClickRefund = useCallback((record: EmployeeAdvance) => {
    setAdvance(record);
    setRefundModal(true);
  }, []);

  const handleDelete = async () => {
    if (advance) {
      try {
        const response = await deleteEmployeeAdvance(Number(advance?.id));
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Could not delete advance.");
      }
    }
  };

  const handleCancel = async () => {
    if (advance) {
      try {
        const response = await cancelEmployeeAdvance(advance.id, cancelReason);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setCancelModal(false);
      } catch (err) {
        toast.error((err as any).response?.data?.message ?? "Could not cancel advance.");
      }
    }
  };

  const handleDisburse = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!advance) return;
    setDisburseSubmitting(true);
    try {
      const response = await disburseEmployeeAdvance(advance.id, payload);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
        setDisburseModal(false);
      } else {
        toast.error(response.message ?? "Could not disburse advance.");
      }
    } catch (err) {
      toast.error((err as any).response?.data?.message ?? "Could not disburse advance.");
    } finally {
      setDisburseSubmitting(false);
    }
  };

  const handleRefund = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    if (!advance) return;
    setRefundSubmitting(true);
    try {
      const response = await refundEmployeeAdvance(advance.id, payload);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
        setRefundModal(false);
      } else {
        toast.error(response.message ?? "Could not refund advance.");
      }
    } catch (err) {
      toast.error((err as any).response?.data?.message ?? "Could not refund advance.");
    } finally {
      setRefundSubmitting(false);
    }
  };

  const columns: ColumnDef<EmployeeAdvance>[] = useMemo(
    () => [
      IndexColumn<EmployeeAdvance>(),
      {
        header: "Code",
        cell: ({ row }) => {
          const r = row.original;
          return canReadAdv ? (
            <Link to={`/finance/employee-advances/${r.id}`} className="text-decoration-none link">
              <CodeCell code={r.code} />
            </Link>
          ) : (
            <CodeCell code={r.code} />
          );
        },
        accessorKey: "code",
        enableColumnFilter: false,
      },
      {
        header: "Employee",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const r = row.original;
          const name = employeeName(r);
          return canReadEmployee && r.employeeId ? (
            <Link to={`/employees/${r.employeeId}`} className="text-decoration-none link">
              {name}
            </Link>
          ) : (
            name
          );
        },
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => formatCurrency(row.original.amount),
      },
      {
        header: "Settled",
        accessorKey: "settledAmount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => formatCurrency(row.original.settledAmount ?? 0),
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge value={row.original.status} map={employeeAdvanceStatusMap} />
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => <DateTime date={row.original.created_at} />,
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isDraft = r.status === EmployeeAdvanceStatus.DRAFT;
          const isActive = r.status === EmployeeAdvanceStatus.PROCESSED;
          const showEdit = canUpdateAdv && isActive;
          const showCancel =
            canCancelAdv && (isDraft || isActive) && (r.settledAmount ?? 0) <= 0;
          const showDisburse = canDisburseAdv && isDraft;
          const showRefund =
            canRefundAdv && isActive && (r.settleableAmount ?? 0) > 0;
          const showDelete =
            canDeleteAdv &&
            (isDraft || r.status === EmployeeAdvanceStatus.CANCELLED);
          const hasMenuItems =
            canReadAdv ||
            showEdit ||
            showCancel ||
            showDisburse ||
            showRefund ||
            showDelete;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }

          const hasItemsBeforeDelete =
            canReadAdv || showEdit || showCancel || showDisburse || showRefund;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Employee advance actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadAdv ? (
                  <DropdownItem tag={Link} to={`/finance/employee-advances/${r.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem tag={Link} to={`/finance/employee-advances/edit/${r.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showDisburse ? (
                  <DropdownItem onClick={() => onClickDisburse(r)}>
                    <i className="ri-hand-coin-line align-bottom me-2 text-success" />
                    Disburse
                  </DropdownItem>
                ) : null}
                {showRefund ? (
                  <DropdownItem onClick={() => onClickRefund(r)}>
                    <i className="ri-arrow-go-back-line align-bottom me-2 text-danger" />
                    Refund
                  </DropdownItem>
                ) : null}
                {showCancel ? (
                  <DropdownItem onClick={() => onClickCancel(r)}>
                    <i className="ri-close-circle-line align-bottom me-2 text-muted" />
                    Cancel
                  </DropdownItem>
                ) : null}
                {showDelete ? (
                  <>
                    {hasItemsBeforeDelete ? <DropdownItem divider /> : null}
                    <DropdownItem className="text-danger" onClick={() => onClickDelete(r)}>
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      onClickDelete,
      onClickCancel,
      onClickDisburse,
      onClickRefund,
      canReadAdv,
      canReadEmployee,
      canUpdateAdv,
      canDeleteAdv,
      canCancelAdv,
      canDisburseAdv,
      canRefundAdv,
    ],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <CancelModal
        show={cancelModal}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onCancelClick={handleCancel}
        onCloseClick={() => {
          setCancelModal(false);
          setCancelReason("");
        }}
        record={advance}
      />

      <DisburseModal
        show={disburseModal}
        record={advance}
        submitting={disburseSubmitting}
        onCloseClick={() => setDisburseModal(false)}
        onConfirm={(payload) => void handleDisburse(payload)}
      />

      <RefundModal
        show={refundModal}
        record={advance}
        submitting={refundSubmitting}
        onCloseClick={() => setRefundModal(false)}
        onConfirm={(payload) => void handleRefund(payload)}
      />

      <TableContainer
        columns={columns}
        fetchApi={getEmployeeAdvances}
        requestParams={requestParams}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        filterCount={filterCount}
        onFiltersClear={() => setStatus("")}
        toolbarRight={
          <EmployeeAdvanceListFilters status={status} onStatusChange={setStatus} />
        }
      />
    </React.Fragment>
  );
};
